import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { getAdminUserDetail } from "@/api/admin";
import { Card, SectionLabel } from "@/components/ui/Card";

export default function AdminUserDetail() {
  const { id } = useParams<{ id: string }>();
  const uid = Number(id);
  const q = useQuery({
    queryKey: ["admin-user", uid],
    queryFn: () => getAdminUserDetail(uid),
    enabled: !Number.isNaN(uid),
  });

  if (q.isLoading) {
    return <div className="py-6 text-center text-taupe">Loading…</div>;
  }
  if (!q.data) {
    return <div className="py-6 text-center text-coral">Not found.</div>;
  }

  const d = q.data;

  return (
    <div className="space-y-5">
      <Link
        to="/admin/users"
        className="inline-flex items-center gap-1 text-[13px] text-taupe hover:text-navy-deep"
      >
        <ArrowLeft className="h-4 w-4" strokeWidth={1.8} />
        <span>Back to users</span>
      </Link>

      <Card>
        <SectionLabel>Profile</SectionLabel>
        <dl className="mt-3 grid grid-cols-2 gap-3 text-[13px] md:grid-cols-4">
          <KV label="Email" value={d.user.email} />
          <KV label="User ID" value={`#${d.user.id}`} />
          <KV label="Language" value={d.user.language} />
          <KV label="Admin" value={d.user.is_admin ? "yes" : "no"} />
          <KV
            label="Joined"
            value={
              d.user.created_at
                ? new Date(d.user.created_at).toISOString().slice(0, 10)
                : "—"
            }
          />
        </dl>
      </Card>

      <Card>
        <SectionLabel>Activity</SectionLabel>
        <div className="mt-3 grid grid-cols-3 gap-3">
          <Counter label="Workouts" value={d.activity.workouts} />
          <Counter label="Meals" value={d.activity.meals} />
          <Counter label="Chat messages" value={d.activity.chat_messages} />
        </div>
      </Card>

      <Card>
        <SectionLabel>Spend · by model</SectionLabel>
        <table className="mt-3 w-full text-[13px]">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-taupe">
              <th className="py-1">Model</th>
              <th className="py-1 text-right">USD</th>
              <th className="py-1 text-right">Calls</th>
              <th className="py-1 text-right">In tok</th>
              <th className="py-1 text-right">Out tok</th>
            </tr>
          </thead>
          <tbody>
            {d.spend.by_model.length === 0 ? (
              <tr>
                <td colSpan={5} className="py-2 text-taupe">
                  No calls yet.
                </td>
              </tr>
            ) : (
              d.spend.by_model.map((r) => (
                <tr key={r.model} className="border-t border-cream-border">
                  <td className="py-1.5">{r.model}</td>
                  <td className="metric-number py-1.5 text-right">
                    ${r.usd.toFixed(4)}
                  </td>
                  <td className="metric-number py-1.5 text-right text-taupe">
                    {r.calls}
                  </td>
                  <td className="metric-number py-1.5 text-right text-taupe">
                    {r.input_tokens.toLocaleString()}
                  </td>
                  <td className="metric-number py-1.5 text-right text-taupe">
                    {r.output_tokens.toLocaleString()}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>

      <Card>
        <SectionLabel>Spend · by operation</SectionLabel>
        <table className="mt-3 w-full text-[13px]">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-taupe">
              <th className="py-1">Operation</th>
              <th className="py-1 text-right">USD</th>
              <th className="py-1 text-right">Calls</th>
            </tr>
          </thead>
          <tbody>
            {d.spend.by_operation.length === 0 ? (
              <tr>
                <td colSpan={3} className="py-2 text-taupe">
                  No calls yet.
                </td>
              </tr>
            ) : (
              d.spend.by_operation.map((r) => (
                <tr key={r.operation} className="border-t border-cream-border">
                  <td className="py-1.5">{r.operation}</td>
                  <td className="metric-number py-1.5 text-right">
                    ${r.usd.toFixed(4)}
                  </td>
                  <td className="metric-number py-1.5 text-right text-taupe">
                    {r.calls}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>

      <Card>
        <SectionLabel>Spend · by month</SectionLabel>
        <table className="mt-3 w-full text-[13px]">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-taupe">
              <th className="py-1">Month</th>
              <th className="py-1 text-right">USD</th>
            </tr>
          </thead>
          <tbody>
            {d.spend.by_month.length === 0 ? (
              <tr>
                <td colSpan={2} className="py-2 text-taupe">
                  No months yet.
                </td>
              </tr>
            ) : (
              d.spend.by_month.map((r) => (
                <tr key={r.month} className="border-t border-cream-border">
                  <td className="py-1.5">{r.month}</td>
                  <td className="metric-number py-1.5 text-right">
                    ${r.usd.toFixed(4)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function KV({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-wider text-taupe">{label}</dt>
      <dd className="mt-0.5 text-navy-deep">{value}</dd>
    </div>
  );
}

function Counter({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-xl border border-cream-border bg-cream px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-taupe">{label}</div>
      <div className="metric-number text-[22px] text-navy-deep">{value}</div>
    </div>
  );
}
